/**
 * Hook to fetch rotation numbers + reference odds from the rotos service (via relay).
 *
 * Replaces useRotationNumbers which fetched from The Odds API browser-side.
 * Data source: Unabated → rotos PostgreSQL → relay /api/rotos/* → this hook.
 */

import { useState, useEffect, useMemo } from 'react';
import type { Sport } from '@/lib/sportsDiscovery';
import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';
import {
  NBA_TEAM_NAMES,
  NHL_TEAM_NAMES,
  NFL_TEAM_NAMES,
  MLB_TEAM_NAMES,
} from '@/lib/nbaConsolidated/teamNames';

// ── Types ─────────────────────────────────────────────────────────────

interface RotosGame {
  event_id: number;
  league_code: string;
  home_team_name: string | null;
  away_team_name: string | null;
  home_rotation: number | null;
  away_rotation: number | null;
  event_date: string;
  start_time_utc: string;
  is_live: boolean;
}

// ── Relay URL (same pattern as relayHttp.ts) ──────────────────────────

const envRelayUrl = (import.meta.env.VITE_RELAY_HTTP_URL as string | undefined)?.trim();
// Base URL for relay — same host, /relay/* prefix for cPanel proxy compatibility
const RELAY_BASE =
  envRelayUrl && envRelayUrl.length > 0
    ? envRelayUrl.replace(/\/relay\/http$/, '')
    : import.meta.env.DEV
      ? 'http://localhost:8787'
      : '';

// ── Team Name Matching ────────────────────────────────────────────────

/** Maps sport code → Record<abbreviation, full name> */
const TEAM_MAPS: Record<string, Record<string, string>> = {
  nba: NBA_TEAM_NAMES,
  nhl: NHL_TEAM_NAMES,
  nfl: NFL_TEAM_NAMES,
  mlb: MLB_TEAM_NAMES,
};

/**
 * Match a dashboard game (with team codes like "LAL") to a rotos game
 * (with full names like "Los Angeles Lakers").
 *
 * Strategy: look up team code → full name, then compare against rotos name.
 * Falls back to substring matching for CBB and unmapped teams.
 */
function matchGame(
  game: ConsolidatedGameBooks,
  rotosGames: RotosGame[],
  sport: Sport
): RotosGame | null {
  const teamMap = TEAM_MAPS[sport];

  // Get expected full names from our team maps
  const expectedHome = teamMap?.[game.homeCode]?.toLowerCase();
  const expectedAway = teamMap?.[game.awayCode]?.toLowerCase();

  for (const rg of rotosGames) {
    // Date must match (event_date may be ISO timestamp or YYYY-MM-DD)
    const rotosDate = rg.event_date?.slice(0, 10);
    if (rotosDate !== game.date) continue;

    const rotosHome = rg.home_team_name?.toLowerCase() ?? '';
    const rotosAway = rg.away_team_name?.toLowerCase() ?? '';

    // Strategy 1: exact full name match via team map
    if (expectedHome && expectedAway) {
      if (rotosHome.includes(expectedHome) && rotosAway.includes(expectedAway)) return rg;
      // Check swapped (Unabated might flip home/away)
      if (rotosHome.includes(expectedAway) && rotosAway.includes(expectedHome)) return rg;
    }

    // Strategy 2: team code as substring (handles CBB like "DUKE" in "Duke Blue Devils")
    const homeCode = game.homeCode.toLowerCase();
    const awayCode = game.awayCode.toLowerCase();
    if (
      homeCode.length >= 3 &&
      awayCode.length >= 3 &&
      rotosHome.includes(homeCode) &&
      rotosAway.includes(awayCode)
    ) {
      return rg;
    }

    // Strategy 3: game.homeName direct match (if populated)
    if (game.homeName && game.awayName) {
      const gHome = game.homeName.toLowerCase();
      const gAway = game.awayName.toLowerCase();
      if (
        (rotosHome.includes(gHome) || gHome.includes(rotosHome)) &&
        (rotosAway.includes(gAway) || gAway.includes(rotosAway))
      ) {
        return rg;
      }
    }
  }

  return null;
}

// ── Hook ──────────────────────────────────────────────────────────────

export function useRotos(sport: Sport, games: ConsolidatedGameBooks[]): ConsolidatedGameBooks[] {
  const [rotosGames, setRotosGames] = useState<RotosGame[]>([]);

  useEffect(() => {
    let cancelled = false;

    const url = `${RELAY_BASE}/relay/rotos/games?sport=${encodeURIComponent(sport)}`;

    fetch(url, { signal: AbortSignal.timeout(10000) })
      .then((res) => {
        if (!res.ok) throw new Error(`Rotos API error: ${res.status}`);
        return res.json() as Promise<{ games: RotosGame[] }>;
      })
      .then((data) => {
        if (!cancelled) setRotosGames(data.games);
      })
      .catch((err) => {
        // Graceful degradation — if rotos is down, games display without rotation numbers
        if (!cancelled) {
          console.warn('[useRotos] Failed to fetch rotos data:', err);
          setRotosGames([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [sport]);

  return useMemo(() => {
    if (!rotosGames.length) return games;

    return games.map((game) => {
      const matched = matchGame(game, rotosGames, sport);
      if (!matched) return game;

      return {
        ...game,
        homeRotation: matched.home_rotation,
        awayRotation: matched.away_rotation,
      };
    });
  }, [games, rotosGames, sport]);
}
